import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import Groups2Icon from "@mui/icons-material/Groups2";
import CloseIcon from "@mui/icons-material/Close";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EmailIcon from "@mui/icons-material/Email";
import ThreePIcon from "@mui/icons-material/ThreeP";
import InfoIconButton from "./InfoIconButtonComponent";
import ProjectTeamTable from "./ProjectTeamComponent";
import { getRequiredRoles } from "../../Containers/continerUtilityFunctions";
import { PROCORE_DOMAIN } from "../../../../utils/config/config";
import "../../static/project-dashboard-styles.css";

// Accept all required props
const ProjectTeamSection = ({
	selectedProject,
	projectData,
	teamData,
	tab,
	roles,
	classes
}) => {
	const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);
	const openRosterModal = () => setIsRosterModalOpen(true);
	const closeRosterModal = () => setIsRosterModalOpen(false);
	// Check for missing project team members

	let missingProjectTeam = false;
	let missingRoles = [];

	if (Array.isArray(teamData)) {
		for (const role of getRequiredRoles()) {
			const roleExists = teamData.some((member) => member.role === role);
			if (!roleExists) {
				missingRoles.push(role);
				missingProjectTeam = true;
			}
		}
	}

	// Email All Handler
	const handleEmailAll = (emails) => {
		const validEmails = emails.filter(Boolean);
		if (validEmails.length === 0) {
		alert("No team members have email addresses.");
		return;
		}
		const subject = encodeURIComponent(
			`Question on ${selectedProject.project_number} ${selectedProject.name}`
		);
		const mailtoLink =
		`mailto:${validEmails.join(",")}` + `?subject=${subject}`;
		window.location.href = mailtoLink;
	};

	return (
        <>
            <div className="card-content-header card-content-header-team">
				<Typography
					variant="subtitle1"
					className="card-title card-title-left-top"
				>
					Project Team
				</Typography>
				<div className="project-team-icon-container">
					{/* Warning icon for missing team members */}
					{missingProjectTeam && selectedProject && (
						<Tooltip
							title={
								<div className="warning-icon-tooltip-content">
									<p style={{ margin: 0 }}>
										Some project team members have missing information. Please review your Procore admin tool and update.
									</p>
									<Button
										variant="contained"
										className="smaller-procore-button"
										size="small"
										onClick={() => {
											window.open(
												`https://${PROCORE_DOMAIN}/${projectData?.project_data?.procore_id}/project/admin`,
												"_blank"
											);
										}}
										sx={{ marginTop: "8px", textAlign: "center" }}
									>
										Open Procore Admin Tool
									</Button>
								</div>
							}
							componentsProps={{
								tooltip: {
									sx: {
										fontSize: "1rem",
										padding: "10px",
									},
								},
							}}
						>
							<WarningIcon className="warning-icon-orange" />
						</Tooltip>
					)}
					{/* Email All Button */}
					{selectedProject && (
						<Button
							variant="contained"
							startIcon={<Groups2Icon />}
							color="primary"
							sx={{ backgroundColor: "#007bff", color: "#fff" }}
							onClick={openRosterModal}
							className="smaller-primary-buttom"
						>
							Full Team
						</Button>
					)}
					<InfoIconButton
						projectData={projectData}
						source="procoreTeam"
						sourceProcore={projectData?.sourceProcore}
					/>
				</div>
			</div>
            <ProjectTeamTable
				selectedProject={selectedProject}
				teamData={teamData}
				reset={tab}
			/>
            {/* Full Team Dialog */}
            {selectedProject && (
				<Dialog
					open={isRosterModalOpen}
					onClose={closeRosterModal}
					maxWidth="md"
					sx={{ overflowY: "scroll" }}
					fullWidth
				>
					{/* Dialog's Close Icon */}
					<IconButton
                        onClick={closeRosterModal}
                        sx={{ color: "#000", position: "absolute", top: "8px", right: "8px" }}
                        size="large">
						<CloseIcon />
					</IconButton>

					<DialogTitle>Full Project Team</DialogTitle>
					<DialogContent>
						<Grid container spacing={{ xs: 2 }}>
							{roles.length > 0 ? (
								roles.map((role, index) => (
									<Grid
										size={{ xs: 12, sm: 6 }}
										key={index}
										className="project-teammember-info-container"
									>
										<div className="project-teammember-card">
											{/* Profile Picture */}
											{role.profile_picture ? (
												<img
													src={role.profile_picture}
													alt={`${role.name || "Role"}'s profile`}
													className="project-team-profile-pic"
												/>
											) : (
												<AccountCircleIcon className="project-team-profile-icon" />
											)}

											{/* Role Name and Email */}
											<div className="project-team-info">
												<div className="project-team-info-inner">
													<Typography
														variant="subtitle1"
														className="titillium-web project-team-role"
													>
														{role.role}
													</Typography>
													<span className="project-team-name-email-row">
														<Typography
															variant="subtitle1"
															className="titillium-web project-team-name"
														>
															{role.name}
														</Typography>
														<Typography
															variant="body2"
															className="project-team-email"
														>
															{role.email && (
																<>
																	<Tooltip
																		title={
																			<div className="warning-icon-tooltip-content">
																				<Typography className="project-team-tooltip-text">{`Teams ${role.name}`}</Typography>
																			</div>
																		}
																	>
																		<a
																			className="project-team-contact-icon"
																			href={`https://teams.microsoft.com/l/chat/0/0?users=${role.email}`}
																			target="_blank"
																			rel="noopener noreferrer"
																		>
																			<IconButton
																				size="small"
																				className={classes.iconButton}
																			>
																				<ThreePIcon
																					className={classes.iconButton + ' project-team-contact-icon'}
																					sx={{ fontSize: "18px", paddingLeft: "0px" }}
																				/>
																			</IconButton>
																		</a>
																	</Tooltip>

																	<Tooltip
																		title={
																			<div className="warning-icon-tooltip-content">
																				<Typography className="project-team-tooltip-text">{`Email ${role.name}`}</Typography>
																			</div>
																		}
																	>
																		<a
																			className="project-team-contact-icon-email"
																			href={`mailto:${role.email}`}
																			target="_blank"
																			rel="noopener noreferrer"
																		>
																			<IconButton
																				size="small"
																				className={classes.iconButton}
																			>
																				<EmailIcon
																					className={
																						classes.iconButton + ' project-team-contact-icon-email'
																					}
																					sx={{ fontSize: "1.20rem", marginLeft: "-2px" }}
																				/>
																			</IconButton>
																		</a>
																	</Tooltip>
																</>
															)}
														</Typography>
													</span>
												</div>
											</div>
										</div>
									</Grid>
								))
							) : (
								<Typography>No team members found.</Typography>
							)}
						</Grid>
					</DialogContent>
					{/* Dialog Actions */}
					<DialogActions>
						<Button
							className="procore-button"
							href={`https://${PROCORE_DOMAIN}/${projectData?.project_data?.procore_id}/project/home`}
							target="_blank"
						>
							Update in Procore
						</Button>
						<Button
							variant="contained"
							color="primary"
							startIcon={<EmailIcon />}
							onClick={() =>
								handleEmailAll(
									roles.map((role) => role.email).filter(Boolean),
									selectedProject?.project_number,
									selectedProject?.name
								)
							}
						>
							Email All
						</Button>
					</DialogActions>
				</Dialog>
			)}
        </>
    );
};

export default ProjectTeamSection;
